import React, { useEffect, useState, useRef } from "react";
import { api, resolveImg } from "../../lib/api";
import { PageHead, Modal, Field, inp } from "../../components/admin/AdminBits";
import RichEditor from "../../components/RichEditor";
import { Plus, Pencil, Trash2, Upload, Newspaper, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { title: "", slug: "", excerpt: "", content: "", cover_image: "", seo_title: "", seo_description: "", seo_keywords: "", published: true };

export default function AdminBlog() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const load = () => api.get("/admin/blog").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const f = e.target.files[0]; if (!f) return; setUploading(true);
    const fd = new FormData(); fd.append("file", f);
    try { const { data } = await api.post("/admin/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setEdit((p) => ({ ...p, cover_image: data.url })); }
    catch { toast.error("Грешка"); } finally { setUploading(false); }
  };
  const save = async (e) => {
    e.preventDefault();
    try { if (edit.id) await api.put(`/admin/blog/${edit.id}`, edit); else await api.post("/admin/blog", edit); toast.success("Запазено"); setEdit(null); load(); }
    catch { toast.error("Грешка"); }
  };
  const del = async (id) => { if (window.confirm("Изтриване?")) { await api.delete(`/admin/blog/${id}`); load(); } };

  return (
    <div data-testid="admin-blog">
      <PageHead title="Блог" desc="Създавайте статии с визуален едитор и код, оптимизирани за SEO."
        action={<button onClick={() => setEdit({ ...EMPTY })} className="btn-gold px-4 py-2.5 rounded-sm text-sm flex items-center gap-2" data-testid="add-post-btn"><Plus size={16} /> Нова статия</button>} />
      <div className="bg-white border border-stone overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-stone text-navy"><tr><th className="text-left p-3">Заглавие</th><th className="text-left p-3">URL</th><th className="text-left p-3">Статус</th><th className="p-3"></th></tr></thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-t border-stone" data-testid={`post-${p.slug}`}>
                <td className="p-3 text-navy font-medium flex items-center gap-2"><Newspaper size={14} className="text-gold" />{p.title}</td>
                <td className="p-3"><a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="text-gold hover:underline flex items-center gap-1">/blog/{p.slug} <ExternalLink size={11} /></a></td>
                <td className="p-3">{p.published ? <span className="text-green-600">Публикувана</span> : <span className="text-charcoal/40">Чернова</span>}</td>
                <td className="p-3 text-right"><div className="flex justify-end gap-2"><button onClick={() => setEdit(p)} className="text-navy hover:text-gold"><Pencil size={15} /></button><button onClick={() => del(p.id)} className="text-charcoal/40 hover:text-red-500"><Trash2 size={15} /></button></div></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={4} className="p-4 text-charcoal/50">Няма статии.</td></tr>}
          </tbody>
        </table>
      </div>
      {edit && (
        <Modal title={edit.id ? "Редакция на статия" : "Нова статия"} onClose={() => setEdit(null)} wide>
          <form onSubmit={save}>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Заглавие"><input required value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} className={inp} data-testid="post-title" /></Field>
              <Field label="URL (slug)"><input value={edit.slug} onChange={(e) => setEdit({ ...edit, slug: e.target.value })} className={inp} placeholder="auto" /></Field>
            </div>
            <Field label="Корица">
              {edit.cover_image && <img src={resolveImg(edit.cover_image)} alt="" className="w-full h-32 object-cover border border-stone mb-2" />}
              <input ref={fileRef} type="file" accept="image/*" onChange={upload} className="hidden" />
              <button type="button" onClick={() => fileRef.current?.click()} className="btn-navy-outline px-4 py-2 rounded-sm text-sm flex items-center gap-2"><Upload size={14} /> {uploading ? "Качване..." : "Качи корица"}</button>
            </Field>
            <Field label="Кратко резюме"><textarea value={edit.excerpt} onChange={(e) => setEdit({ ...edit, excerpt: e.target.value })} rows={2} className={inp} /></Field>
            <Field label="Съдържание"><RichEditor value={edit.content} onChange={(v) => setEdit({ ...edit, content: v })} /></Field>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <Field label="SEO заглавие"><input value={edit.seo_title} onChange={(e) => setEdit({ ...edit, seo_title: e.target.value })} className={inp} /></Field>
              <Field label="SEO ключови думи"><input value={edit.seo_keywords} onChange={(e) => setEdit({ ...edit, seo_keywords: e.target.value })} className={inp} /></Field>
            </div>
            <Field label="SEO описание"><textarea value={edit.seo_description} onChange={(e) => setEdit({ ...edit, seo_description: e.target.value })} rows={2} className={inp} /></Field>
            <label className="flex items-center gap-2 text-sm text-navy"><input type="checkbox" checked={edit.published} onChange={(e) => setEdit({ ...edit, published: e.target.checked })} className="accent-[#C6A45C]" /> Публикувана</label>
            <button type="submit" className="btn-gold w-full py-2.5 rounded-sm mt-4" data-testid="save-post-btn">Запази</button>
          </form>
        </Modal>
      )}
    </div>
  );
}
